import { FileRejection } from '@mantine/dropzone';
import React from 'react';
import { getErrorMessage, logger, triggerDownload } from 'src/utils';
import { IAttachedFile } from 'src/design/organisms/attachments/attachmentTypes';
import { uploadBatch } from 'src/design/organisms/attachments/uploadBatch';

export interface IAttachmentUploadOptions {
  uploadFile: (file: File) => Promise<IAttachedFile>;
  downloadFile: (id: string) => Promise<{ url: string; name: string }>;
  /** Called once per drop with the files that landed; never called with an empty list. */
  onUploaded: (uploaded: IAttachedFile[]) => void;
}

export interface IAttachmentUpload {
  loading: boolean;
  /** Every user-facing failure line since the last drop gesture or clearProblems() call: rejections, upload failures, and download failures all append to the same list. */
  problems: string[];
  clearProblems: () => void;
  onDrop: (accepted: File[]) => Promise<void>;
  onReject: (rejections: FileRejection[]) => void;
  download: (file: IAttachedFile) => Promise<void>;
}

/** @registry Owns the shared attachment-uploader state machine for FileDrop and ImageUpload: drop/reject intake, batch upload via uploadBatch, and downloads. The problem list is cleared once per drop gesture and by clearProblems(); every failure line in between (rejections, upload failures, download failures) appends. */
export const useAttachmentUpload = ({
  uploadFile,
  downloadFile,
  onUploaded,
}: IAttachmentUploadOptions): IAttachmentUpload => {
  const [loading, setLoading] = React.useState(false);
  const [problems, setProblems] = React.useState<string[]>([]);

  // onDrop is memoized on mount-stable deps only; without this ref, the
  // onDrop instance bound to the Dropzone at drop time would close over
  // whichever onUploaded (and therefore whichever `files`) existed when
  // that drop started, resurrecting anything removed while the upload
  // was still in flight.
  const onUploadedRef = React.useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const gestureStarted = React.useRef(false);
  // One drop gesture can deliver rejections (sync) and upload failures (async);
  // react-dropzone fires both callbacks in the same task, so the previous
  // gesture's problems are cleared exactly once and this gesture's lines append.
  const beginGesture = React.useCallback(() => {
    if (gestureStarted.current) return;
    gestureStarted.current = true;
    setProblems([]);
    queueMicrotask(() => {
      gestureStarted.current = false;
    });
  }, []);

  const onDrop = React.useCallback(
    async (accepted: File[]) => {
      beginGesture();
      setLoading(true);
      try {
        const { uploaded, failures } = await uploadBatch(accepted, uploadFile);
        failures.forEach(({ file, error }) =>
          logger.error('Failed to upload attachment', {
            err: error,
            fileName: file.name,
          }),
        );
        if (failures.length > 0) {
          setProblems((prev) => [
            ...prev,
            ...failures.map(
              ({ file, error }) => `${file.name}: ${getErrorMessage(error)}`,
            ),
          ]);
        }
        if (uploaded.length > 0) onUploadedRef.current(uploaded);
      } finally {
        setLoading(false);
      }
    },
    [beginGesture, uploadFile],
  );

  const onReject = React.useCallback(
    (rejections: FileRejection[]) => {
      beginGesture();
      setProblems((prev) => [
        ...prev,
        ...rejections.map((r) => `${r.file.name}: ${r.errors[0].message}`),
      ]);
    },
    [beginGesture],
  );

  const download = React.useCallback(
    async (file: IAttachedFile) => {
      try {
        const result = await downloadFile(file.id);
        triggerDownload(result.url, result.name);
      } catch (err) {
        logger.error('Failed to download attachment', { err, id: file.id });
        setProblems((prev) => [
          ...prev,
          `${file.name ?? file.id}: ${getErrorMessage(err)}`,
        ]);
      }
    },
    [downloadFile],
  );

  const clearProblems = React.useCallback(() => setProblems([]), []);

  return { loading, problems, clearProblems, onDrop, onReject, download };
};
